import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, TextField, Box, Typography, IconButton, Menu, MenuItem,
  Checkbox, ListItemText, Tooltip, Divider, Chip, Badge,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, Stack, Paper,
  FormControlLabel, CircularProgress, FormControl, FormLabel, RadioGroup, Radio,
  Select, InputLabel,
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import RestoreIcon from '@mui/icons-material/Restore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MicIcon from '@mui/icons-material/Mic';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import { TableKit, OPERATORS } from '@NP-Webber/crm-kit';
import api from '../../services/api.js';
import AudioPlayer from '../../components/AudioPlayer.jsx';
import ImportSection from '../../components/ImportSection.jsx';
import GemaraSelectionEditor, {
  selectionToEntries,
  entriesToSelection,
  isGemaraEntriesValid,
} from '../../components/GemaraSelectionEditor.jsx';
import MemberCard from './MemberCard.jsx';
import { buildMemberGradesMap } from '../../utils/gradeSummary.js';
import MemberReportIssueDialog from './reports/MemberReportIssueDialog.jsx';
import MemberReportTemplateEditor from './reports/MemberReportTemplateEditor.jsx';
import { GROUP_BY_OPTIONS } from './reports/memberReportMergeFields.js';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { formatHebrewDateTime } from '../../utils/hebrewDate';
import { useCkInside } from '@NP-Webber/crm-kit/dist/next/index.js';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';
import { useQuickCreate } from '../../next/quickCreate';
import { useScreenSettings } from '../../next/related/relatedSettingsStore';

const IS_OPEN_GEMARA = process.env.REACT_APP_EXAM_MODE === 'open-gemara';
const SYSTEM_ID = String(process.env.REACT_APP_SYSTEM_ID || '').replace(/^["']|["']$/g, '').trim();
const IS_MISHTAMERET = SYSTEM_ID === 'waiting_client';
const PHONE_ALT_LABEL = IS_MISHTAMERET ? 'טלפון אשה' : 'טלפון נוסף';

const FULL_EXPORT_GROUP_LABELS = Object.fromEntries(
  GROUP_BY_OPTIONS.map((o) => [o.value, o.label])
);

const OPERATOR_LABELS = Object.fromEntries(
  (OPERATORS || []).map((op) => [op.key, op.label])
);

const OCCUPATION_OPTIONS = IS_MISHTAMERET
  ? ['', 'עמל הפרנסה', 'כולל', 'ישיבת היכל התורה']
  : ['', 'בעה"ב', 'אברך כולל'];

/** סיכום סינוני עמודות מתוך filterState של TableKit */
function buildColumnFilterSummary(filterState, columns) {
  if (!filterState) return [];
  const colTitle = (key) => columns.find((c) => c.key === key)?.title || key;
  const parts = [];
  const {
    filters = {},
    pinnedFilters = {},
    valueFilters = {},
    conditionFilters = {},
    globalSearch = '',
  } = filterState;

  if (String(globalSearch || '').trim()) {
    parts.push(`חיפוש בטבלה: ${String(globalSearch).trim()}`);
  }

  Object.entries(filters).forEach(([key, text]) => {
    if (String(text || '').trim()) {
      parts.push(`${colTitle(key)}: ${String(text).trim()}`);
    }
  });

  Object.entries(pinnedFilters).forEach(([key, pins]) => {
    if (Array.isArray(pins) && pins.length) {
      parts.push(`${colTitle(key)}: ${pins.join(', ')}`);
    }
  });

  Object.entries(valueFilters).forEach(([key, vals]) => {
    if (Array.isArray(vals) && vals.length) {
      const shown = vals.length > 8
        ? `${vals.slice(0, 8).join(', ')} ועוד ${vals.length - 8}`
        : vals.join(', ');
      parts.push(`${colTitle(key)}: ${shown}`);
    }
  });

  Object.entries(conditionFilters).forEach(([key, cond]) => {
    if (!cond?.operator) return;
    const label = OPERATOR_LABELS[cond.operator] || cond.operator;
    if (cond.operator === 'empty' || cond.operator === 'not_empty') {
      parts.push(`${colTitle(key)} ${label}`);
    } else if (cond.operator === 'between') {
      parts.push(`${colTitle(key)} ${label} ${cond.value ?? ''}–${cond.value2 ?? ''}`);
    } else {
      parts.push(`${colTitle(key)} ${label} ${cond.value ?? ''}`.trim());
    }
  });

  return parts;
}

const HIDDEN_MEMBER_COLUMNS = [
  'registered',
  ...(!IS_OPEN_GEMARA ? ['kolel'] : []),
  'kolelMorning',
  'kolelAfternoon',
  'kolelEvening',
  'kolelMorningTrack',
  'kolelAfternoonTrack',
  'kolelFullDay',
];

/* ─── שם חבר — בצבע שונה כשיש פער נתונים שלא טופל ─── */
const DATA_ISSUE_COLOR = '#c62828';
function MemberName({ row, children }) {
  if (!row?.dataIssue) return children ?? null;
  return (
    <Tooltip title={row.dataIssue}>
      <Box component="span" sx={{ color: DATA_ISSUE_COLOR, fontWeight: 600 }}>{children}</Box>
    </Tooltip>
  );
}

/* ─── כל העמודות האפשריות (לפי המודל) ─── */
const ALL_COLUMNS = [
  { key: 'firstName', title: 'שם פרטי', sortable: true, filterable: true, minWidth: '100px', render: (v, row) => <MemberName row={row}>{v}</MemberName> },
  { key: 'lastName', title: 'משפחה', sortable: true, filterable: true, minWidth: '100px', render: (v, row) => <MemberName row={row}>{v}</MemberName> },
  { key: 'fatherName', title: 'אביו', sortable: true, filterable: true, minWidth: '90px' },
  { key: 'fatherInLawName', title: 'חתן', sortable: true, filterable: true, minWidth: '90px' },
  { key: 'phone', title: 'נייד', sortable: false, filterable: true, minWidth: '110px' },
  { key: 'phoneAlt', title: PHONE_ALT_LABEL, sortable: false, filterable: true, minWidth: '110px' },
  { key: 'idNumber', title: 'מ.ז.', sortable: true, filterable: true, minWidth: '90px' },
  { key: 'occupation', title: 'עיסוק', sortable: true, filterable: true, minWidth: '120px' },
  ...(IS_OPEN_GEMARA
    ? [{ key: 'kolel', title: 'כולל', sortable: true, filterable: true, minWidth: '120px' }]
    : []),
  { key: 'city', title: 'ישוב', sortable: true, filterable: true, minWidth: '80px' },
  { key: 'street', title: 'רחוב', sortable: true, filterable: true, minWidth: '90px' },
  { key: 'buildingNumber', title: 'מספר בניין', sortable: false, filterable: false, minWidth: '70px' },
  { key: 'entrance', title: 'כניסה', sortable: false, filterable: false, minWidth: '60px' },
  { key: 'apartment', title: 'דירה', sortable: false, filterable: false, minWidth: '60px' },
  {
    key: 'status',
    title: 'סטטוס',
    sortable: true,
    filterable: true,
    minWidth: '80px',
    render: (val) => (
      <Chip
        label={val === 'active' ? 'פעיל' : 'לא פעיל'}
        size="small"
        color={val === 'active' ? 'primary' : 'default'}
        variant={val === 'active' ? 'filled' : 'outlined'}
        sx={{ fontWeight: 600 }}
      />
    ),
  },
  { key: 'personalCode', title: 'קוד אישי', sortable: true, minWidth: '80px', render: (v) => v != null ? String(v).padStart(4, '0') : '—' },
  {
    key: '_examRegistration',
    title: 'רשום למבחן',
    sortable: true,
    filterable: true,
    minWidth: '120px',
    render: (v) => v || '—',
  },
  {
    key: '_examined',
    title: 'נבחן',
    sortable: true,
    filterable: true,
    minWidth: '80px',
    render: (v) => v ? (
      <Chip label={v} size="small" color="success" variant="outlined" sx={{ fontWeight: 600 }} />
    ) : '—',
  },
  {
    key: '_gradesSummary',
    title: 'ציונים',
    sortable: true,
    filterable: true,
    minWidth: '140px',
    render: (v, row) => {
      const grades = row._grades;
      if (!grades?.length) return '—';
      return (
        <Tooltip title={v}>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ maxWidth: 280 }}>
            {grades.map((g, i) => (
              <Chip
                key={i}
                label={`${g.label}: ${g.score}`}
                size="small"
                color={g.score >= 90 ? 'success' : 'default'}
                variant={g.score >= 90 ? 'filled' : 'outlined'}
                sx={{ fontWeight: g.score >= 90 ? 700 : 400 }}
              />
            ))}
          </Stack>
        </Tooltip>
      );
    },
  },
  {
    key: '_actions',
    title: 'פעולות',
    sortable: false,
    filterable: false,
    width: '100px',
    render: (_, row) => null,
  },
];

/* ברירת מחדל — עמודות שמוצגות בהתחלה */
const DEFAULT_VISIBLE = [
  '_examined', '_gradesSummary', 'firstName', 'lastName', 'idNumber', 'occupation',
  ...(IS_OPEN_GEMARA ? ['kolel'] : []),
  'phone', 'city',
  'personalCode', 'status', '_actions',
];

const STORAGE_KEY = 'members_visible_columns';

const FIELD_LABEL = {
  firstName: 'שם פרטי', lastName: 'שם משפחה',
  fatherName: 'שם האב', fatherInLawName: 'שם השווער',
  city: 'ישוב', street: 'רחוב',
};

/** props ל-ExtendedCard של crm-kit — פרטי חבר + טאבי ציונים/בקשות */
function buildMemberExtendedCardProps(row, { onEdit } = {}) {
  const detailsFields = [
    { key: 'firstName', label: 'שם פרטי', value: row.firstName },
    { key: 'lastName', label: 'שם משפחה', value: row.lastName },
    { key: 'fatherName', label: 'אב', value: row.fatherName },
    { key: 'fatherInLawName', label: 'חותן', value: row.fatherInLawName },
    { key: 'idNumber', label: 'מספר זהות', value: row.idNumber },
    { key: 'phone', label: 'נייד', value: row.phone },
    { key: 'phoneAlt', label: PHONE_ALT_LABEL, value: row.phoneAlt },
    {
      key: 'personalCode',
      label: 'קוד אישי',
      value: row.personalCode != null ? String(row.personalCode).padStart(4, '0') : '',
    },
    { key: 'occupation', label: 'עיסוק', value: row.occupation },
    ...(IS_OPEN_GEMARA ? [{ key: 'kolel', label: 'כולל', value: row.kolel }] : []),
    { key: 'city', label: 'ישוב', value: row.city },
    { key: 'street', label: 'רחוב', value: row.street },
    { key: 'buildingNumber', label: 'מספר בניין', value: row.buildingNumber },
    { key: 'entrance', label: 'כניסה', value: row.entrance },
    { key: 'apartment', label: 'דירה', value: row.apartment },
  ];

  const grades = row._grades || [];
  const pending = (row.pendingUpdates || []).filter((u) => u.status === 'pending');

  const extraTabs = [
    {
      label: grades.length ? `ציונים (${grades.length})` : 'ציונים',
      content: grades.length ? (
        <Stack spacing={1}>
          {grades.map((g, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                py: 1,
                px: 1.5,
                borderRadius: 1,
                bgcolor: 'var(--ck-surface-2, #f8fafd)',
                border: '1px solid var(--ck-border, #e3eaf5)',
              }}
            >
              <Typography sx={{ fontSize: 13, color: 'var(--ck-text, #1a2533)', fontWeight: 600 }}>
                {g.label}
              </Typography>
              <Chip
                label={g.score}
                size="small"
                color={g.score >= 90 ? 'success' : 'default'}
                variant={g.score >= 90 ? 'filled' : 'outlined'}
                sx={{ fontWeight: 700, minWidth: 44 }}
              />
            </Box>
          ))}
        </Stack>
      ) : (
        <Typography sx={{ color: 'var(--ck-text-2, #5c7a9e)', fontSize: 14, py: 2 }}>
          אין ציונים להצגה
        </Typography>
      ),
    },
  ];

  if (pending.length) {
    extraTabs.push({
      label: `בקשות עדכון (${pending.length})`,
      content: (
        <Stack spacing={1}>
          {pending.map((u, i) => (
            <Box
              key={u._id || i}
              sx={{
                py: 1,
                px: 1.5,
                borderRadius: 1,
                bgcolor: 'var(--ck-warning-soft, #fff8e1)',
                border: '1px solid var(--ck-warning-line, #ffe082)',
              }}
            >
              <Typography sx={{ fontSize: 13, color: 'var(--ck-text, #1a2533)', fontWeight: 700 }}>
                {FIELD_LABEL[u.field] || u.label || u.field}
              </Typography>
              {u.requestedAt && (
                <Typography sx={{ fontSize: 12, color: 'var(--ck-text-2, #5c7a9e)', mt: 0.3 }}>
                  {formatHebrewDateTime(u.requestedAt)}
                </Typography>
              )}
              {u.recordingUrl && (
                <Typography sx={{ fontSize: 12, color: 'var(--ck-text-2, #5c7a9e)', mt: 0.3 }}>
                  יש הקלטה — לאישור מלא לחצו ערוך
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      ),
    });
  }

  return {
    title: `${row.firstName || ''} ${row.lastName || ''}`.trim(),
    statusBadge:
      row.status === 'active'
        ? { label: 'פעיל', color: '#fff', bg: '#2e7d32' }
        : { label: 'לא פעיל', color: 'var(--ck-text-2, #5c7a9e)', bg: 'var(--ck-neutral-soft, #e8eef5)' },
    hideFeedTab: true,
    detailsFields,
    extraTabs,
    headerActions: onEdit
      ? [
          {
            icon: <OpenInNewIcon fontSize="small" />,
            tooltip: 'ערוך',
            onClick: () => onEdit(row),
          },
        ]
      : [],
  };
}

function loadVisibleColumns() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      let changed = false;
      const filtered = parsed.filter((k) => !HIDDEN_MEMBER_COLUMNS.includes(k));
      if (filtered.length !== parsed.length) {
        parsed.length = 0;
        parsed.push(...filtered);
        changed = true;
      }

      if (!parsed.includes('occupation')) {
        const idIdx = parsed.indexOf('idNumber');
        const insertAt = idIdx >= 0 ? idIdx + 1 : parsed.length;
        parsed.splice(insertAt, 0, 'occupation');
        changed = true;
      }
      if (IS_OPEN_GEMARA && !parsed.includes('kolel')) {
        const occIdx = parsed.indexOf('occupation');
        const insertAt = occIdx >= 0 ? occIdx + 1 : parsed.length;
        parsed.splice(insertAt, 0, 'kolel');
        changed = true;
      }
      if (!parsed.includes('_gradesSummary')) {
        const examinedIdx = parsed.indexOf('_examined');
        if (examinedIdx >= 0) parsed.splice(examinedIdx + 1, 0, '_gradesSummary');
        else parsed.push('_gradesSummary');
        changed = true;
      }
      if (changed) localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
      return parsed;
    }
  } catch { /* ignore */ }
  return DEFAULT_VISIBLE;
}

const MembersList = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canReadGrades = hasPermission('grades', 'read');
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [pinnedSearches, setPinnedSearches] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const inNextDesign = useCkInside();
  useQuickCreate(() => setSelectedId('new'));
  const [pendingOnly, setPendingOnly] = useState(false);
  const [examinedOnly, setExaminedOnly] = useState(false);

  // ── Dialog בקשות עדכון inline ─────────────────────────────────────
  const [pendingDialog, setPendingDialog] = useState({ open: false, member: null });
  const [approveValues, setApproveValues] = useState({});
  const [resolveLoading, setResolveLoading] = useState({});
  const [resolveError,   setResolveError]   = useState('');

  // ── מחיקת חבר ───────────────────────────────────────────────
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  // ── דיאלוג סימון נבחן לפי חבר ────────────────────────────
  const [activePeriodId, setActivePeriodId] = useState(null);
  const [activePeriodName, setActivePeriodName] = useState('');
  const [memberExamineDialog, setMemberExamineDialog] = useState(null);
  // null | { memberId, memberName }
  const [memberExamineRegs, setMemberExamineRegs] = useState([]);
  const [memberExamineLoading, setMemberExamineLoading] = useState(false);
  const [memberExamineChanges, setMemberExamineChanges] = useState({});
  // Hilcheta: { [regId]: boolean }
  // Talmuda:  { [regId]: entries[] } — מצב עורך טווחים + נבחן
  const [memberExamineSaving, setMemberExamineSaving] = useState(false);
  const [memberExamineSnackbar, setMemberExamineSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [fullExportLoading, setFullExportLoading] = useState(false);
  const [fullExportDialogOpen, setFullExportDialogOpen] = useState(false);
  const [fullExportReport, setFullExportReport] = useState('full'); // full | kolels-material | members-material
  const [fullExportMode, setFullExportMode] = useState('single'); // single | byKey
  const [fullExportGroupBy, setFullExportGroupBy] = useState('kolel');
  const [fullExportPeriodIds, setFullExportPeriodIds] = useState([]);
  const [fullExportFilterPeriods, setFullExportFilterPeriods] = useState(false);
  const [reportIssueOpen, setReportIssueOpen] = useState(false);
  const [reportTemplatesOpen, setReportTemplatesOpen] = useState(false);
  // "הגדרות קשורות" — the settings that live inside this screen
  useScreenSettings([
    { key: 'members-report-design', title: 'עיצוב ורקע לדוחות', description: 'תבניות הדוחות וכרטיסי הנבחן שמונפקים מרשימת החברים', icon: 'edit', onClick: () => setReportTemplatesOpen(true) },
  ]);
  const [reportSelectedIds, setReportSelectedIds] = useState([]);
  const [tableFilteredRows, setTableFilteredRows] = useState(null);
  const [tableFilterState, setTableFilterState] = useState(null);
  const [examPeriods, setExamPeriods] = useState([]);
  const [occupationSaving, setOccupationSaving] = useState({});
  // ── צינתוק לכל החברים ─────────────────────────────────────────────
  const [tzintukOpen, setTzintukOpen] = useState(false);
  const [tzintukPreview, setTzintukPreview] = useState(null);
  const [tzintukLoading, setTzintukLoading] = useState(false);
  const [tzintukSending, setTzintukSending] = useState(false);
  const [tzintukError, setTzintukError] = useState('');
  const [tzintukResult, setTzintukResult] = useState(null);
  // יעד הצינתוק נקבע בפתיחת הדיאלוג: שורות שנבחרו → הסינון בטבלה → כל הפעילים
  const [tzintukTarget, setTzintukTarget] = useState(null);

  const openTzintukDialog = async () => {
    let target;
    if (reportSelectedIds.length) {
      target = { memberIds: reportSelectedIds.map(String), label: `${reportSelectedIds.length} שורות שנבחרו` };
    } else if (reportRows.length < allData.length) {
      target = { memberIds: filteredMemberIds, label: `${reportRows.length} חברים לפי הסינון` };
    } else {
      target = { memberIds: undefined, label: 'כל החברים הפעילים' };
    }
    setTzintukTarget(target);
    setTzintukOpen(true);
    setTzintukPreview(null);
    setTzintukError('');
    setTzintukResult(null);
    setTzintukLoading(true);
    try {
      const { data } = await api.post('/members/tzintuk/preview', { memberIds: target.memberIds });
      setTzintukPreview(data);
    } catch (e) {
      setTzintukError(e.response?.data?.error || 'שגיאה בבדיקת יתרת יחידות');
    } finally {
      setTzintukLoading(false);
    }
  };

  const sendTzintuk = async () => {
    setTzintukSending(true);
    setTzintukError('');
    try {
      const { data } = await api.post('/members/tzintuk', { memberIds: tzintukTarget?.memberIds });
      setTzintukResult(data);
    } catch (e) {
      setTzintukError(e.response?.data?.error || 'שגיאה בשליחת צינתוק');
    } finally {
      setTzintukSending(false);
    }
  };

  const openPendingDialog = (row) => {
    setPendingDialog({ open: true, member: row });
    setApproveValues({});
    setResolveError('');
  };
  const closePendingDialog = () => setPendingDialog({ open: false, member: null });

  const handleResolve = async (memberId, updateId, action, value) => {
    setResolveLoading((p) => ({ ...p, [updateId]: true }));
    setResolveError('');
    try {
      await api.post(`/members/${memberId}/pending-updates/${updateId}/resolve`,
        { action, ...(action === 'approve' ? { value } : {}) });
      // עדכן allData במקום בלי reload מלא
      setAllData((prev) => prev.map((m) => {
        if (String(m._id) !== String(memberId)) return m;
        return {
          ...m,
          pendingUpdates: (m.pendingUpdates || []).map((u) =>
            String(u._id) === String(updateId)
              ? { ...u, status: action === 'approve' ? 'approved' : 'rejected',
                  ...(action === 'approve' && value ? { [pendingDialog.member.field]: value } : {}) }
              : u
          ),
        };
      }));
    } catch (e) {
      setResolveError(e.response?.data?.error || 'שגיאה בעיבוד הבקשה');
    } finally {
      setResolveLoading((p) => ({ ...p, [updateId]: false }));
    }
  };

  const handleDeleteMember = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/members/${deleteTarget._id}`);
      setAllData((prev) => prev.filter((m) => m._id !== deleteTarget._id));
      setDeleteTarget(null);
    } catch (e) {
      console.error('שגיאה במחיקת חבר:', e);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleOccupationChange = async (row, value) => {
    if (!row?._id || value === (row.occupation || '')) return;
    const memberId = String(row._id);
    setOccupationSaving((prev) => ({ ...prev, [memberId]: true }));
    try {
      const { data } = await api.put(`/members/${memberId}`, {
        version: row.version,
        occupation: value,
      });
      setAllData((prev) => prev.map((m) =>
        String(m._id) === memberId
          ? { ...m, occupation: data?.occupation || '', version: data?.version ?? m.version }
          : m
      ));
      setMemberExamineSnackbar({ open: true, message: 'העיסוק עודכן', severity: 'success' });
    } catch (e) {
      setMemberExamineSnackbar({
        open: true,
        message: e.response?.data?.error || 'שגיאה בעדכון עיסוק',
        severity: 'error',
      });
    } finally {
      setOccupationSaving((prev) => ({ ...prev, [memberId]: false }));
    }
  };

  /* ─── סימון פער נתונים כטופל (מנקה dataIssue — הסיבה נשמרת ביומן) ─── */
  const handleMarkIssueHandled = useCallback(async (row) => {
    if (!row?._id || !row.dataIssue) return;
    if (!window.confirm(`לסמן כטופל את ${row.firstName || ''} ${row.lastName || ''}?\n\n${row.dataIssue}`)) return;
    const memberId = String(row._id);
    try {
      const { data } = await api.put(`/members/${memberId}`, { version: row.version, dataIssue: '' });
      setAllData((prev) => prev.map((m) =>
        String(m._id) === memberId ? { ...m, dataIssue: '', version: data?.version ?? m.version } : m
      ));
      setMemberExamineSnackbar({ open: true, message: 'סומן כטופל', severity: 'success' });
    } catch (e) {
      setMemberExamineSnackbar({ open: true, message: e.response?.data?.error || 'שגיאה בסימון כטופל', severity: 'error' });
    }
  }, []);

  /* ─── פתח דיאלוג סימון נבחן לפי חבר ─── */
  const openMemberExamineDialog = useCallback((row) => {
    const regs = row._regs || [];
    setMemberExamineDialog({ memberId: row._id, memberName: `${row.firstName} ${row.lastName}` });
    setMemberExamineRegs(regs);
    const init = {};
    if (IS_OPEN_GEMARA) {
      regs.forEach((r) => {
        init[r._id] = selectionToEntries(r.gemaraSelection);
      });
    } else {
      regs.forEach((r) => { init[r._id] = r.examined || false; });
    }
    setMemberExamineChanges(init);
  }, []);

  const memberExamineGemaraValid = IS_OPEN_GEMARA
    && memberExamineRegs.every((reg) => isGemaraEntriesValid(memberExamineChanges[reg._id]));

  const handleMemberExamineSave = async () => {
    if (IS_OPEN_GEMARA && !memberExamineGemaraValid) return;
    setMemberExamineSaving(true);
    try {
      const savedByRegId = {};
      for (const reg of memberExamineRegs) {
        const change = memberExamineChanges[reg._id];
        if (IS_OPEN_GEMARA) {
          const gemaraSelection = entriesToSelection(change);
          const { data: updated } = await api.put(`/registrations/${reg._id}`, { gemaraSelection });
          savedByRegId[reg._id] = updated.gemaraSelection;
        } else if (change !== undefined && change !== (reg.examined || false)) {
          await api.put(`/registrations/${reg._id}`, { examined: change });
          savedByRegId[reg._id] = change;
        }
      }
      setAllData((prev) => prev.map((m) => {
        if (String(m._id) !== String(memberExamineDialog.memberId)) return m;
        const updatedRegs = (m._regs || []).map((r) => {
          if (IS_OPEN_GEMARA) {
            const updatedGs = savedByRegId[r._id];
            return updatedGs ? { ...r, gemaraSelection: updatedGs } : r;
          }
          const change = memberExamineChanges[r._id];
          return { ...r, examined: change !== undefined ? change : r.examined };
        });
        let newExamined = '';
        if (IS_OPEN_GEMARA) {
          const exTractates = updatedRegs.flatMap((r) =>
            (r.gemaraSelection || []).filter((s) => s.examined).map((s) => s.tractate)
          );
          newExamined = exTractates.join(', ');
        } else {
          newExamined = updatedRegs.some((r) => r.examined) ? 'כן' : '';
        }
        return { ...m, _regs: updatedRegs, _examined: newExamined };
      }));
      setMemberExamineSnackbar({ open: true, message: 'סטטוס בחינה עודכן', severity: 'success' });
      setMemberExamineDialog(null);
    } catch {
      setMemberExamineSnackbar({ open: true, message: 'שגיאה בשמירת סטטוס בחינה', severity: 'error' });
    } finally {
      setMemberExamineSaving(false);
    }
  };

  /* ─── חיפוש חי (debounce) ─── */
  const debounceRef = useRef(null);
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setActiveSearch(searchInput.trim());
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  /* ─── בחירת עמודות ─── */
  const [visibleKeys, setVisibleKeys] = useState(loadVisibleColumns);
  const [anchorEl, setAnchorEl] = useState(null);

  const toggleColumn = (key) => {
    setVisibleKeys((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const showAll = () => {
    const all = ALL_COLUMNS.map((c) => c.key);
    setVisibleKeys(all);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  };

  const resetDefaults = () => {
    setVisibleKeys(DEFAULT_VISIBLE);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_VISIBLE));
  };

  /* ─── עמודות מסוננות + פעולות ─── */
  const activeColumns = useMemo(() => {
    return ALL_COLUMNS
      .filter((col) => visibleKeys.includes(col.key))
      .map((col) => {
        if (col.key === '_gradesSummary') {
          return {
            ...col,
            title: activePeriodName ? `ציונים (${activePeriodName})` : col.title,
          };
        }
        if (col.key === 'occupation') {
          return {
            ...col,
            render: (value, row) => {
              const memberId = String(row._id || '');
              return (
                <Select
                  size="small"
                  value={value || ''}
                  displayEmpty
                  disabled={!!occupationSaving[memberId]}
                  onClick={(e) => e.stopPropagation()}
                  onDoubleClick={(e) => e.stopPropagation()}
                  onChange={(e) => handleOccupationChange(row, e.target.value)}
                  sx={{ minWidth: 118, bgcolor: 'var(--ck-surface, #fff)' }}
                >
                  <MenuItem value="">בחר</MenuItem>
                  {[...OCCUPATION_OPTIONS.filter(Boolean), ...(value && !OCCUPATION_OPTIONS.includes(value) ? [value] : [])].map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </Select>
              );
            },
          };
        }
        if (col.key === '_actions') {
          return {
            ...col,
            render: (_, row) => {
              const pending = (row.pendingUpdates || []).filter((u) => u.status === 'pending');
              return (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<OpenInNewIcon fontSize="inherit" />}
                    onClick={(e) => { e.stopPropagation(); setSelectedId(row._id); }}
                  >
                    ערוך
                  </Button>
                  {(row._regs || []).length > 0 && (
                    <Tooltip title="סמן נבחן">
                      <IconButton
                        size="small"
                        color="success"
                        onClick={(e) => { e.stopPropagation(); openMemberExamineDialog(row); }}
                      >
                        <AssignmentTurnedInIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {row.dataIssue && (
                    <Tooltip title={`סמן כטופל — ${row.dataIssue}`}>
                      <IconButton
                        size="small"
                        color="success"
                        onClick={(e) => { e.stopPropagation(); handleMarkIssueHandled(row); }}
                      >
                        <CheckCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="מחק חבר">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(row); }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {pending.length > 0 && (
                    <Tooltip title={`${pending.length} בקשות עדכון ממתינות`}>
                      <Badge badgeContent={pending.length} color="warning">
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={(e) => { e.stopPropagation(); openPendingDialog(row); }}
                        >
                          <MicIcon fontSize="small" />
                        </IconButton>
                      </Badge>
                    </Tooltip>
                  )}
                </Stack>
              );
            },
          };
        }
        return col;
      });
  }, [visibleKeys, activePeriodName, setSelectedId, openMemberExamineDialog, occupationSaving, handleMarkIssueHandled]);

  /* ─── טעינת כל הנתונים פעם אחת ─── */
  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      const settle = (p) => p.then((r) => r).catch(() => null);
      const [membersRes, regsRes, periodsRes, gradesRes] = await Promise.all([
        api.get('/members', { params: { page: 1, pageSize: 100000 } }),
        settle(api.get('/registrations', { params: { page: 1, pageSize: 100000 } })),
        settle(api.get('/exam-periods')),
        canReadGrades
          ? settle(api.get('/grades', { params: { all: 1 } }))
          : Promise.resolve(null),
      ]);

      // מציאים את המועד הרלוונטי: הפעיל, או אם אין פעיל — האחרון לפי סדר
      const allPeriods = periodsRes?.data?.data || [];
      setExamPeriods(allPeriods);
      const activePer = allPeriods.find(p => p.isActive);
      const lastPeriod = activePer || (allPeriods.length > 0 ? allPeriods[allPeriods.length - 1] : null);
      const lastPeriodId = lastPeriod?._id || null;
      setActivePeriodId(lastPeriodId);
      setActivePeriodName(lastPeriod?.name || '');

      const gradesMap = canReadGrades
        ? buildMemberGradesMap(
          gradesRes?.data?.data || [],
          lastPeriodId,
          { isGemara: IS_OPEN_GEMARA },
        )
        : {};

      // מיפוי מזהה חבר → הרשמות (גולמיות) למועד האחרון
      const memberRegsMap = {}; // { [memberId]: reg[] }

      // מיפוי מזהה חבר → שם מבחן + סטטוס נבחן (רק הרשמות למועד האחרון)
      const regMap = {};       // label
      const examinedMap = {};  // examined label
      for (const reg of (regsRes?.data?.data || [])) {
        if (reg.status !== 'registered') continue;
        // סנן רק רישום ששייך למועד האחרון (periodId יכול להיות object מ-populate)
        const regPeriodId = reg.periodId?._id ? String(reg.periodId._id) : String(reg.periodId || '');
        if (lastPeriodId && regPeriodId !== String(lastPeriodId)) continue;
        const mid = String(typeof reg.memberId === 'object' ? reg.memberId?._id : reg.memberId);
        if (!mid || mid === 'undefined') continue;
        // שמור הרשמות גולמיות לפי חבר
        if (!memberRegsMap[mid]) memberRegsMap[mid] = [];
        memberRegsMap[mid].push(reg);
        const codePrefix = reg.examId?.code != null ? `[${reg.examId.code}] ` : '';
        const label = codePrefix + (reg.examId?.name || (reg.gemaraSelection?.length ? reg.gemaraSelection.map(s => s.tractate).join(', ') : ''));
        if (label) {
          regMap[mid] = regMap[mid] ? `${regMap[mid]}, ${label}` : label;
        }
        // חישוב נבחן
        if (reg.gemaraSelection?.length) {
          // תלמודא — מסכתות שנבחן בהן
          const examinedTractates = reg.gemaraSelection.filter(s => s.examined).map(s => s.tractate);
          if (examinedTractates.length > 0) {
            const txt = examinedTractates.join(', ');
            examinedMap[mid] = examinedMap[mid] ? `${examinedMap[mid]}, ${txt}` : txt;
          }
        } else if (reg.examined) {
          // הלכתא — נבחן ✓
          const exLabel = codePrefix + (reg.examId?.name || 'כן');
          examinedMap[mid] = examinedMap[mid] ? `${examinedMap[mid]}, ${exLabel}` : exLabel;
        }
      }
      const data = (membersRes.data.data || []).map((m) => {
        const memberKey = String(m._id);
        const gradeInfo = gradesMap[memberKey] || { summary: '', items: [] };
        return {
        ...m,
        _examRegistration: regMap[memberKey] || '',
        _examined: examinedMap[memberKey] || '',
        _regs: memberRegsMap[memberKey] || [],
        _gradesSummary: gradeInfo.summary,
        _grades: gradeInfo.items,
      };
      });
      setAllData(data);
    } catch (err) {
      console.error('שגיאה בשליפת חברים:', err);
    } finally {
      setLoading(false);
    }
  }, [canReadGrades]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const pinSearch = () => {
    const val = searchInput.trim();
    if (!val || pinnedSearches.includes(val)) return;
    setPinnedSearches((prev) => [...prev, val]);
    setSearchInput('');
    setActiveSearch('');
  };

  const removePinnedSearch = (val) => {
    setPinnedSearches((prev) => prev.filter((p) => p !== val));
  };

  const clearAllSearch = () => {
    setSearchInput('');
    setActiveSearch('');
    setPinnedSearches([]);
  };

  /* ─── סינון חיפוש בצד הלקוח — רק בעמודות הגלויות ─── */
  const filteredData = useMemo(() => {
    const terms = [...pinnedSearches, activeSearch].filter(Boolean);
    let result = allData;
    if (terms.length > 0) {
      const searchFields = visibleKeys.filter((k) => k !== '_actions');
      result = result.filter((row) =>
        terms.every((term) => {
          const words = term.trim().split(/\s+/).filter(Boolean);
          return words.every((word) => {
            const lower = word.toLowerCase();
            return searchFields.some((f) =>
              String(row[f] || '').toLowerCase().includes(lower)
            );
          });
        })
      );
    }
    if (pendingOnly) {
      result = result.filter((row) =>
        (row.pendingUpdates || []).some((u) => u.status === 'pending')
      );
    }
    if (examinedOnly) {
      result = result.filter((row) => !!row._examined);
    }
    return result;
  }, [allData, pinnedSearches, activeSearch, visibleKeys, pendingOnly, examinedOnly]);

  const handleTableFilteredChange = useCallback((payload) => {
    const nextRows = payload?.rows || [];
    const nextState = payload?.filterState || null;
    setTableFilteredRows((prev) => {
      if (
        prev &&
        prev.length === nextRows.length &&
        prev.every((row, i) => String(row?._id) === String(nextRows[i]?._id))
      ) {
        return prev;
      }
      return nextRows;
    });
    setTableFilterState((prev) => {
      try {
        if (JSON.stringify(prev) === JSON.stringify(nextState)) return prev;
      } catch {
        /* ignore */
      }
      return nextState;
    });
  }, []);

  /* שורות אחרי סינון TableKit (עמודות) — עד שה-callback רץ משתמשים בסינון ההורה */
  const reportRows = tableFilteredRows ?? filteredData;

  const filteredMemberIds = useMemo(
    () => reportRows.map((r) => String(r._id)),
    [reportRows]
  );

  const reportFilterSummary = useMemo(() => {
    const parts = [];
    if (pinnedSearches.length) parts.push(`חיפוש: ${pinnedSearches.join(', ')}`);
    if (activeSearch.trim()) parts.push(`חיפוש: ${activeSearch.trim()}`);
    if (pendingOnly) parts.push('בקשות עדכון בלבד');
    if (examinedOnly) parts.push('נבחנים בלבד');
    if (activePeriodName) parts.push(`מועד: ${activePeriodName}`);
    parts.push(...buildColumnFilterSummary(tableFilterState, ALL_COLUMNS));
    return parts;
  }, [pinnedSearches, activeSearch, pendingOnly, examinedOnly, activePeriodName, tableFilterState]);

  const pendingCount = useMemo(() =>
    allData.filter((r) => (r.pendingUpdates || []).some((u) => u.status === 'pending')).length,
  [allData]);

  /* ─── חלונית צפה בלחיצה כפולה ─── */
  const renderDrawerDetail = useCallback((row) => {
    const Section = ({ title, children }) => (
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ color: 'var(--ck-primary, #1565c0)', fontWeight: 700, letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
          {title}
        </Typography>
        {children}
      </Box>
    );
    const Field = ({ label, value }) =>
      value ? (
        <Box sx={{ display: 'flex', gap: 1, py: 0.4, borderBottom: '1px solid var(--ck-border, #f0f4f8)' }}>
          <Box sx={{ minWidth: 130, color: 'var(--ck-text-2, #5c7a9e)', fontWeight: 600, fontSize: 12 }}>{label}</Box>
          <Box sx={{ color: 'var(--ck-text, #1a2533)', fontSize: 13 }}>{value}</Box>
        </Box>
      ) : null;

    return (
      <Box>
        <Box sx={{ mb: 2, pb: 1.5, borderBottom: '1px solid var(--ck-border, #e3eaf5)' }}>
          <Typography variant="h6" fontWeight={700}>
            <MemberName row={row}>{row.firstName} {row.lastName}</MemberName>
          </Typography>
          <Chip
            label={row.status === 'active' ? 'פעיל' : 'לא פעיל'}
            size="small"
            color={row.status === 'active' ? 'primary' : 'default'}
            variant={row.status === 'active' ? 'filled' : 'outlined'}
            sx={{ mt: 0.5, fontWeight: 600 }}
          />
        </Box>

        <Section title="פרטים אישיים">
          <Field label="שם פרטי" value={row.firstName} />
          <Field label="שם משפחה" value={row.lastName} />
          <Field label="אב" value={row.fatherName} />
          <Field label="חותן" value={row.fatherInLawName} />
          <Field label="מספר זהות" value={row.idNumber} />
          <Field label="נייד" value={row.phone} />
          <Field label={PHONE_ALT_LABEL} value={row.phoneAlt} />
          <Field label="קוד אישי" value={row.personalCode != null ? String(row.personalCode).padStart(4, '0') : ''} />
          <Field label="עיסוק" value={row.occupation} />
        </Section>

        <Section title="כתובת">
          <Field label="ישוב" value={row.city} />
          <Field label="רחוב" value={row.street} />
          <Field label="מספר בניין" value={row.buildingNumber} />
          <Field label="כניסה" value={row.entrance} />
          <Field label="דירה" value={row.apartment} />
        </Section>

        <Button
          fullWidth
          variant="outlined"
          startIcon={<OpenInNewIcon />}
          onClick={() => setSelectedId(row._id)}
          sx={{ mt: 1, display: 'none' }}
        >
          פתח כרטיסיה מלאה
        </Button>
      </Box>
    );
  }, [setSelectedId]);

  /* ─── ערכים ייחודיים לסינון אקסל — מהנתונים המקומיים ─── */
  const fetchValuesFor = useCallback((colKey) => {
    if (colKey === '_actions') return undefined;
    return () => {
      const seen = new Set();
      let hasEmpty = false;
      for (const r of allData) {
        const raw = r[colKey];
        if (raw === null || raw === undefined || raw === '') {
          hasEmpty = true;
          continue;
        }
        seen.add(String(raw));
      }
      const unique = [...seen].sort((a, b) => a.localeCompare(b, 'he'));
      if (hasEmpty) unique.unshift('');
      return Promise.resolve(unique);
    };
  }, [allData]);

  const handleFullExport = async () => {
    const needsPeriods =
      fullExportReport === 'kolels-material'
      || fullExportReport === 'members-material'
      || (fullExportReport === 'full' && fullExportFilterPeriods);

    if (needsPeriods && fullExportPeriodIds.length === 0) {
      setMemberExamineSnackbar({ open: true, message: 'יש לבחור לפחות מועד אחד', severity: 'error' });
      return;
    }
    if (fullExportReport === 'full' && fullExportMode === 'byKey' && !fullExportGroupBy) {
      setMemberExamineSnackbar({ open: true, message: 'יש לבחור מפתח לפיצול', severity: 'error' });
      return;
    }

    setFullExportLoading(true);
    try {
      const day = new Date().toISOString().slice(0, 10);
      const isZip =
        fullExportMode === 'byKey'
        && (fullExportReport === 'full' || fullExportReport === 'members-material');

      let params = { report: fullExportReport };
      let filename;

      if (fullExportReport === 'kolels-material') {
        params.periodIds = fullExportPeriodIds.join(',');
        params.mode = 'single';
        filename = `כוללים_חומר_${day}.xlsx`;
      } else if (fullExportReport === 'members-material') {
        params.periodIds = fullExportPeriodIds.join(',');
        params.mode = isZip ? 'byKey' : 'single';
        params.groupBy = 'kolel';
        filename = isZip ? `חברים_חומר_${day}.zip` : `חברים_חומר_${day}.xlsx`;
      } else {
        params.mode = isZip ? 'byKey' : 'single';
        if (isZip) params.groupBy = fullExportGroupBy;
        if (fullExportFilterPeriods && fullExportPeriodIds.length > 0) {
          params.periodIds = fullExportPeriodIds.join(',');
        }
        const groupLabel = FULL_EXPORT_GROUP_LABELS[fullExportGroupBy] || fullExportGroupBy;
        filename = isZip
          ? `חברים_מלא_לפי_${groupLabel}_${day}.zip`
          : `חברים_מלא_${day}.xlsx`;
      }

      const response = await api.get('/members/export-excel', { params, responseType: 'blob' });
      const mime = isZip
        ? 'application/zip'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const url = URL.createObjectURL(new Blob([response.data], { type: mime }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      setFullExportDialogOpen(false);
    } catch (err) {
      console.error('שגיאה בייצוא אקסל:', err);
      let message = 'שגיאה בייצוא אקסל';
      try {
        const text = await err.response?.data?.text?.();
        if (text) {
          const parsed = JSON.parse(text);
          if (parsed?.error) message = parsed.error;
        }
      } catch { /* ignore */ }
      setMemberExamineSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setFullExportLoading(false);
    }
  };

  const openFullExportDialog = () => {
    const sorted = [...examPeriods].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const active = sorted.find((p) => p.isActive) || sorted[sorted.length - 1] || null;
    setFullExportReport('full');
    setFullExportMode('single');
    setFullExportGroupBy('kolel');
    setFullExportFilterPeriods(false);
    setFullExportPeriodIds(active?._id ? [String(active._id)] : []);
    setFullExportDialogOpen(true);
  };

  return (
    <Box>
      <AdminPageHeader
        title="רשימת חברים"
        sub={[
          activePeriodName ? `ציונים למועד: ${activePeriodName}` : null,
          !loading ? `${filteredData.length.toLocaleString('he-IL')} חברים` : null,
        ].filter(Boolean).join(' · ')}
        primary={{ label: 'חבר חדש', onClick: () => setSelectedId('new') }}
        menus={[{
          key: 'reports',
          label: 'ייצוא ודוחות',
          icon: 'download',
          items: [
            { key: 'full-export', label: 'ייצוא אקסל מלא', icon: 'download', onClick: openFullExportDialog, disabled: fullExportLoading },
            { key: 'report', label: 'הנפקת דוח', icon: 'file', onClick: () => setReportIssueOpen(true) },
            { key: 'report-design', label: 'התאם עיצוב ורקע', icon: 'edit', onClick: () => setReportTemplatesOpen(true) },
          ],
        }]}
        more={[
          { key: 'tzintuk', label: 'שליחת צינתוק', icon: 'phone', onClick: openTzintukDialog },
        ]}
        legacy={(
          <>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              רשימת חברים
            </Typography>
            {activePeriodName && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                ציונים למועד: {activePeriodName}
              </Typography>
            )}
          </>
        )}
      />

      {/* כרטיס חבר — מודל צף.
          disableEnforceFocus: מאפשר בחירה ב-Autocomplete (כוללים) שרשימתו ב-portal מחוץ לדיאלוג */}
      <Dialog
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
        maxWidth="lg"
        fullWidth
        disableEnforceFocus
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <DialogContent sx={{ p: 3 }}>
          <MemberCard
            recordId={selectedId}
            onClose={(reason) => {
              setSelectedId(null);
              if (reason === 'created') loadAllData();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* דיאלוג ייצוא אקסל מלא */}
      <Dialog
        open={fullExportDialogOpen}
        onClose={() => !fullExportLoading && setFullExportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>ייצוא אקסל מלא</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">סוג ייצוא</FormLabel>
              <RadioGroup
                value={fullExportReport}
                onChange={(e) => {
                  const next = e.target.value;
                  setFullExportReport(next);
                  if (next === 'kolels-material') {
                    setFullExportMode('single');
                  }
                  if (next === 'members-material') {
                    setFullExportGroupBy('kolel');
                  }
                }}
              >
                <FormControlLabel
                  value="full"
                  control={<Radio size="small" />}
                  label="רשומה מלאה — חברים עם כל ההרשמות והציונים"
                  disabled={fullExportLoading}
                />
                <FormControlLabel
                  value="kolels-material"
                  control={<Radio size="small" />}
                  label="רשימת כוללים עם חומר נבחן"
                  disabled={fullExportLoading}
                />
                <FormControlLabel
                  value="members-material"
                  control={<Radio size="small" />}
                  label="רשימת חברים עם חומר נבחן"
                  disabled={fullExportLoading}
                />
              </RadioGroup>
            </FormControl>

            {fullExportReport !== 'kolels-material' && (
              <FormControl component="fieldset">
                <FormLabel component="legend">מצב ייצוא</FormLabel>
                <RadioGroup
                  value={fullExportMode}
                  onChange={(e) => setFullExportMode(e.target.value)}
                >
                  <FormControlLabel
                    value="single"
                    control={<Radio size="small" />}
                    label="ייצוא רגיל — קובץ אחד"
                    disabled={fullExportLoading}
                  />
                  <FormControlLabel
                    value="byKey"
                    control={<Radio size="small" />}
                    label={
                      fullExportReport === 'members-material'
                        ? 'ייצוא לפי כולל — ZIP (אקסל לכל כולל)'
                        : 'ייצוא לפי מפתח — ZIP (אקסל לכל ערך)'
                    }
                    disabled={fullExportLoading}
                  />
                </RadioGroup>
              </FormControl>
            )}

            {fullExportReport === 'full' && fullExportMode === 'byKey' && (
              <FormControl fullWidth size="small">
                <InputLabel id="full-export-groupby-label">מפתח לפיצול</InputLabel>
                <Select
                  labelId="full-export-groupby-label"
                  label="מפתח לפיצול"
                  value={fullExportGroupBy}
                  onChange={(e) => setFullExportGroupBy(e.target.value)}
                  disabled={fullExportLoading}
                >
                  {GROUP_BY_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {fullExportReport === 'full' && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={fullExportFilterPeriods}
                    onChange={(e) => setFullExportFilterPeriods(e.target.checked)}
                    disabled={fullExportLoading}
                  />
                }
                label="סנן לפי מועדים (הרשמות וציונים)"
              />
            )}

            {(fullExportReport === 'kolels-material'
              || fullExportReport === 'members-material'
              || (fullExportReport === 'full' && fullExportFilterPeriods)) && (
              <FormControl fullWidth size="small">
                <InputLabel id="full-export-periods-label">מועדים</InputLabel>
                <Select
                  labelId="full-export-periods-label"
                  label="מועדים"
                  multiple
                  value={fullExportPeriodIds}
                  onChange={(e) => setFullExportPeriodIds(
                    typeof e.target.value === 'string'
                      ? e.target.value.split(',')
                      : e.target.value
                  )}
                  disabled={fullExportLoading}
                  renderValue={(selected) => {
                    const labels = selected.map((id) => {
                      const p = examPeriods.find((x) => String(x._id) === String(id));
                      return p?.name || id;
                    });
                    return labels.join(', ');
                  }}
                >
                  {examPeriods.map((p) => (
                    <MenuItem key={p._id} value={String(p._id)}>
                      <Checkbox checked={fullExportPeriodIds.includes(String(p._id))} size="small" />
                      <ListItemText primary={`${p.name}${p.isActive ? ' (פעיל)' : ''}`} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Typography variant="body2" color="text.secondary">
              {fullExportReport === 'kolels-material' &&
                'קובץ אקסל: קוד/שם כולל, מועד, קוד/שם מבחן וחומר נבחן (נושא + סימנים).'}
              {fullExportReport === 'members-material' && (
                fullExportMode === 'byKey'
                  ? 'ZIP לפי כולל — לכל כולל אקסל עם חבריו והחומר שנבחנים עליו במועדים שנבחרו.'
                  : 'אקסל עם חברים, מבחנים וחומר נבחן למועדים שנבחרו.'
              )}
              {fullExportReport === 'full' && (
                fullExportMode === 'byKey'
                  ? 'ZIP עם אקסל מלא (חברים, הרשמות, ציונים) לכל ערך של המפתח.'
                  : 'אקסל אחד עם כל החברים, ההרשמות והציונים (כולל חומר נבחן).'
              )}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFullExportDialogOpen(false)} disabled={fullExportLoading}>
            ביטול
          </Button>
          <Button
            variant="contained"
            onClick={handleFullExport}
            disabled={
              fullExportLoading
              || (fullExportReport === 'full' && fullExportMode === 'byKey' && !fullExportGroupBy)
              || (
                (fullExportReport === 'kolels-material'
                  || fullExportReport === 'members-material'
                  || (fullExportReport === 'full' && fullExportFilterPeriods))
                && fullExportPeriodIds.length === 0
              )
            }
            startIcon={fullExportLoading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
          >
            ייצוא
          </Button>
        </DialogActions>
      </Dialog>

      {/* חיפוש + כפתורים */}
      <Box sx={{ mb: 2 }}>
        <Box
          component="form"
          onSubmit={(e) => { e.preventDefault(); pinSearch(); }}
          sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}
        >
          <TextField
            size="small"
            placeholder="חיפוש חופשי בכל השדות…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); pinSearch(); } }}
            sx={{ width: 280 }}
          />
          <Tooltip title="נעץ מונח חיפוש (Enter)">
            <span>
              <Button
                type="submit"
                variant="contained"
                size="small"
                disabled={!searchInput.trim()}
              >
                📌 נעץ
              </Button>
            </span>
          </Tooltip>
          {(pinnedSearches.length > 0 || searchInput.trim()) && (
            <Button variant="text" size="small" color="error" onClick={clearAllSearch}>
              ✕ נקה חיפוש
            </Button>
          )}
          <Tooltip title="הצג רק חברים עם בקשות עדכון ממתינות">
            <Badge badgeContent={pendingCount} color="warning" invisible={pendingCount === 0}>
              <Button
                variant={pendingOnly ? 'contained' : 'outlined'}
                color="warning"
                size="small"
                startIcon={<MicIcon />}
                onClick={() => setPendingOnly((v) => !v)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                בקשות עדכון
              </Button>
            </Badge>
          </Tooltip>
          <Tooltip title="הצג רק חברים שנבחנו במועד הנוכחי">
            <Button
              variant={examinedOnly ? 'contained' : 'outlined'}
              color="success"
              size="small"
              startIcon={<AssignmentTurnedInIcon />}
              onClick={() => setExaminedOnly((v) => !v)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              נבחנים
            </Button>
          </Tooltip>
          {!inNextDesign && (
            <>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setSelectedId('new')}
          >
            + חבר חדש
          </Button>

          <Tooltip title="ייצוא אקסל — רשומה מלאה, כוללים/חברים עם חומר נבחן, ZIP לפי כולל">
            <span>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={openFullExportDialog}
                disabled={fullExportLoading}
                sx={{ whiteSpace: 'nowrap' }}
              >
                ייצוא אקסל מלא
              </Button>
            </span>
          </Tooltip>

          <Button
            variant="contained"
            size="small"
            startIcon={<DescriptionIcon />}
            onClick={() => setReportIssueOpen(true)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            הנפקת דוח
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditNoteIcon />}
            onClick={() => setReportTemplatesOpen(true)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            התאם עיצוב ורקע
          </Button>
          <Tooltip title="צינתוק (בלי הודעה) לנייד הבעל — לשורות שנבחרו, למי שמופיע בסינון, או לכל הפעילים">
            <Button
              variant="outlined"
              color="warning"
              size="small"
              startIcon={<PhoneCallbackIcon />}
              onClick={openTzintukDialog}
              sx={{ whiteSpace: 'nowrap' }}
            >
              שליחת צינתוק
            </Button>
          </Tooltip>

            </>
          )}

          {/* כפתור בחירת עמודות */}
          <Tooltip title="בחר עמודות להצגה">
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} color="primary">
              <ViewColumnIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            PaperProps={{ sx: { maxHeight: 480, width: 260 } }}
          >
            <MenuItem dense onClick={showAll}>
              <CheckCircleOutlineIcon sx={{ fontSize: 18, mr: 1, color: 'var(--ck-primary, #1565c0)' }} />
              <ListItemText primary="הצג הכל" />
            </MenuItem>
            <MenuItem dense onClick={resetDefaults}>
              <RestoreIcon sx={{ fontSize: 18, mr: 1, color: 'var(--ck-primary, #1565c0)' }} />
              <ListItemText primary="ברירת מחדל" />
            </MenuItem>
            <Divider />
            {ALL_COLUMNS.filter((c) => c.key !== '_actions').map((col) => (
              <MenuItem key={col.key} dense onClick={() => toggleColumn(col.key)}>
                <Checkbox checked={visibleKeys.includes(col.key)} size="small" sx={{ p: 0, mr: 1 }} />
                <ListItemText primary={col.title} />
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Chips — מונחי חיפוש נעוצים */}
        {pinnedSearches.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 1 }}>
            {pinnedSearches.map((p) => (
              <Chip
                key={p}
                label={p}
                size="small"
                color="primary"
                onDelete={() => removePinnedSearch(p)}
                sx={{ fontWeight: 600 }}
              />
            ))}
          </Box>
        )}
      </Box>

      <TableKit
        columns={activeColumns}
        data={filteredData}
        total={filteredData.length}
        loading={loading}
        clientSideMode
        selectable
        onSelectionChange={setReportSelectedIds}
        onFilteredChange={handleTableFilteredChange}
        showColumnPicker={false}
        emptyMessage="לא נמצאו חברים"
        exportFileName="חברים"
        renderDrawerDetail={renderDrawerDetail}
        onOpenCard={(row) => setSelectedId(row._id)}
        cardVariant={IS_MISHTAMERET ? 'extended' : 'basic'}
        extendedCardProps={
          IS_MISHTAMERET
            ? (row) => buildMemberExtendedCardProps(row, {
                onEdit: (r) => setSelectedId(r._id),
              })
            : undefined
        }
        fetchValuesFor={fetchValuesFor}
      />

      <MemberReportIssueDialog
        open={reportIssueOpen}
        onClose={() => setReportIssueOpen(false)}
        filteredMemberIds={filteredMemberIds}
        filterSummary={reportFilterSummary}
        filteredCount={reportRows.length}
        selectedIds={reportSelectedIds}
        periods={examPeriods}
      />
      <MemberReportTemplateEditor
        open={reportTemplatesOpen}
        onClose={() => setReportTemplatesOpen(false)}
        initialTargetType="list"
      />

      <ImportSection
        label="חברים"
        endpoint="/import/members"
        templateEndpoint="/import/template/members"
        description={'ייבוא רשימת חברים. עמודות: שם פרטי, משפחה, אביו, חתן, נייד, כתובת (ישוב/רחוב/מספר/כניסה/דירה), וקוד כולל לפי סדר (לפנה"צ / אחה"צ / ערב).'}
        onImportDone={() => loadAllData()}
        entityType="members"
        identifierColumns={[
          { field: 'phone', label: 'נייד (טלפון)' },
          { field: 'idNumber', label: 'מ.ז. (תעודת זהות)' },
          { field: 'personalCode', label: 'קוד אישי' },
        ]}
      />

      {/* ── Dialog בקשות עדכון inline ───────────────────────────────────── */}
      <Dialog
        open={pendingDialog.open}
        onClose={closePendingDialog}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MicIcon color="warning" />
          בקשות עדכון — {pendingDialog.member?.firstName} {pendingDialog.member?.lastName}
        </DialogTitle>
        <DialogContent dividers>
          {resolveError && <Alert severity="error" sx={{ mb: 2 }}>{resolveError}</Alert>}
          {(() => {
            const member = pendingDialog.member;
            if (!member) return null;
            const items = (member.pendingUpdates || []).filter((u) => u.status === 'pending');
            if (items.length === 0) return <Alert severity="success">אין בקשות ממתינות</Alert>;
            return (
              <Stack spacing={2}>
                {items.map((item) => (
                  <Paper key={item._id} variant="outlined" sx={{ p: 2 }}>
                    <Stack direction="row" alignItems="flex-start" spacing={2} flexWrap="wrap">
                      <Box flex={1} minWidth={120}>
                        <Typography variant="caption" color="text.secondary">שדה לעדכון</Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {FIELD_LABEL[item.field] || item.label || item.field}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.requestedAt ? formatHebrewDateTime(item.requestedAt) : ''}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">הקלטה</Typography>
                        <Box>{item.recordingUrl
                          ? <AudioPlayer path={item.recordingUrl} />
                          : <Typography variant="body2">—</Typography>}
                        </Box>
                      </Box>
                    </Stack>
                    <Box mt={1.5}>
                      <TextField
                        size="small"
                        fullWidth
                        label="ערך חדש (הקלידו לשמירה)"
                        value={approveValues[item._id] || ''}
                        onChange={(e) => setApproveValues((p) => ({ ...p, [item._id]: e.target.value }))}
                        placeholder={`לדוגמא: ${FIELD_LABEL[item.field] || item.field} החדש`}
                      />
                    </Box>
                    <Stack direction="row" spacing={1} mt={1}>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleOutlineIcon />}
                        disabled={!approveValues[item._id]?.trim() || resolveLoading[item._id]}
                        onClick={() => handleResolve(member._id, item._id, 'approve', approveValues[item._id])}
                      >
                        {resolveLoading[item._id] ? 'שומר...' : 'אשר ועדכן'}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<CancelOutlinedIcon />}
                        disabled={resolveLoading[item._id]}
                        onClick={() => handleResolve(member._id, item._id, 'reject')}
                      >
                        דחה
                      </Button>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePendingDialog}>סגור</Button>
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            onClick={() => { closePendingDialog(); setSelectedId(pendingDialog.member?._id); }}
          >
            פתח כרטיס מלא
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog שליחת צינתוק ───────────────────────────────────────── */}
      <Dialog
        open={tzintukOpen}
        onClose={() => !tzintukSending && setTzintukOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>שליחת צינתוק</DialogTitle>
        <DialogContent>
          {tzintukLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
          ) : tzintukResult && !tzintukResult.sent ? (
            <Alert severity="error">
              הצינתוק לא נשלח{tzintukResult.errors?.length ? `: ${tzintukResult.errors[0]}` : ''}
            </Alert>
          ) : tzintukResult ? (
            <Alert severity={tzintukResult.failed ? 'warning' : 'success'}>
              נשלח צינתוק ל־{tzintukResult.sent} מספרים
              {tzintukResult.failed ? `, נכשלו ${tzintukResult.failed}` : ''}
              {tzintukResult.errors?.length ? ` (${tzintukResult.errors[0]})` : ''}
            </Alert>
          ) : (
            <>
              {tzintukPreview && (
                <Stack spacing={0.5} sx={{ mb: 2 }}>
                  <Typography>
                    נמענים: <strong>{tzintukTarget?.label}</strong>
                  </Typography>
                  <Typography>
                    חברים עם נייד תקין: <strong>{tzintukPreview.count}</strong>
                    {tzintukPreview.memberCount > tzintukPreview.count && ` (מתוך ${tzintukPreview.memberCount})`}
                  </Typography>
                  <Typography>
                    עלות: <strong>{tzintukPreview.cost}</strong> יחידות (0.1 למספר)
                  </Typography>
                  <Typography>
                    יתרת היחידות כרגע: <strong>{Number(tzintukPreview.units).toFixed(1)}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    הצינתוק נשלח לנייד הבעל בלבד, בלי הודעה.
                  </Typography>
                </Stack>
              )}
              {tzintukPreview && !tzintukPreview.enough && (
                <Alert severity="error">אין מספיק יחידות לשליחת הצינתוק. לא יישלח.</Alert>
              )}
              {tzintukPreview && tzintukPreview.count === 0 && (
                <Alert severity="info">אין חברים עם נייד תקין.</Alert>
              )}
            </>
          )}
          {tzintukError && <Alert severity="error" sx={{ mt: 1 }}>{tzintukError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTzintukOpen(false)} disabled={tzintukSending}>
            {tzintukResult ? 'סגור' : 'ביטול'}
          </Button>
          {!tzintukResult && (
            <Button
              variant="contained"
              color="warning"
              onClick={sendTzintuk}
              disabled={
                tzintukSending || tzintukLoading || !tzintukPreview?.enough || !tzintukPreview?.count
              }
            >
              {tzintukSending ? 'שולח...' : `שלח צינתוק ל־${tzintukPreview?.count ?? 0}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Dialog מחיקת חבר ──────────────────────────────────────────── */}
      <Dialog open={!!deleteTarget} onClose={() => !deleteLoading && setDeleteTarget(null)}>
        <DialogTitle sx={{ color: 'var(--ck-danger, #d32f2f)' }}>מחיקת חבר לצמיתות</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            שימו לב: פעולה זו אינה ניתנת לביטול!
          </Alert>
          <Typography>
            האם אתה בטוח שברצונך למחוק לצמיתות את החבר{' '}
            <strong>{deleteTarget?.firstName} {deleteTarget?.lastName}</strong>
            {deleteTarget?.phone ? ` (${deleteTarget.phone})` : ''}?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            כל הנתונים של החבר יימחקו ולא ניתן יהיה לשחזרם.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteLoading}>ביטול</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteMember}
            disabled={deleteLoading}
          >
            {deleteLoading ? 'מוחק...' : 'מחק לצמיתות'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── דיאלוג סימון נבחן לפי חבר ─── */}
      <Dialog open={!!memberExamineDialog} onClose={() => !memberExamineSaving && setMemberExamineDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>סמן נבחן — {memberExamineDialog?.memberName}</DialogTitle>
        <DialogContent>
          {memberExamineLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
          ) : memberExamineRegs.length === 0 ? (
            <Typography color="text.secondary">אין הרשמות פעילות למועד הנוכחי</Typography>
          ) : IS_OPEN_GEMARA ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                ערוך את טווחי הדפים וסמן את המסכתות שהנרשם נבחן בהן בפועל:
              </Typography>
              {memberExamineRegs.map((reg) => (
                <Box key={reg._id}>
                  {memberExamineRegs.length > 1 && (
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      הרשמה{reg.periodId?.name ? ` — ${reg.periodId.name}` : ''}
                    </Typography>
                  )}
                  <GemaraSelectionEditor
                    value={memberExamineChanges[reg._id] || []}
                    onChange={(next) => setMemberExamineChanges((prev) => ({
                      ...prev,
                      [reg._id]: next,
                    }))}
                    showExamined
                  />
                </Box>
              ))}
            </Stack>
          ) : (
            /* הלכתא — checkbox אחד לכל מבחן */
            <Stack spacing={1} sx={{ mt: 1 }}>
              {memberExamineRegs.map((reg) => (
                <FormControlLabel
                  key={reg._id}
                  control={
                    <Checkbox
                      checked={!!(memberExamineChanges[reg._id])}
                      onChange={(e) => setMemberExamineChanges((prev) => ({ ...prev, [reg._id]: e.target.checked }))}
                      size="small"
                      color="success"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {reg.examId?.name || '—'}
                      {reg.periodId?.name ? <Typography component="span" variant="caption" color="text.secondary"> ({reg.periodId.name})</Typography> : ''}
                    </Typography>
                  }
                />
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMemberExamineDialog(null)} disabled={memberExamineSaving}>ביטול</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleMemberExamineSave}
            disabled={
              memberExamineSaving
              || memberExamineRegs.length === 0
              || (IS_OPEN_GEMARA && !memberExamineGemaraValid)
            }
          >
            {memberExamineSaving ? <CircularProgress size={20} /> : 'שמור'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Alert
        severity={memberExamineSnackbar.severity}
        sx={{ display: memberExamineSnackbar.open ? 'flex' : 'none', position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 2000 }}
        onClose={() => setMemberExamineSnackbar((s) => ({ ...s, open: false }))}
      >
        {memberExamineSnackbar.message}
      </Alert>

    </Box>
  );
};

export default MembersList;
